import React, { lazy, Suspense, use, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm, useStore } from "@tanstack/react-form";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import Form from "src/components/base/form";
import { ErrorMessage, Field, FieldGroup, Fieldset, Label, RequiredLabel } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/20/solid";
import { ApiUser } from "src/api/api_user";
import { toast } from "react-toastify/unstyled";
import { genTotpSecret, genTotpUrl } from "src/utils/totp";
import { handleFormError } from "src/utils/form-error";
import { QRCodeSVG } from "qrcode.react";
import { Code, Text } from "src/components/base/text";
import { loadPasswordStrengthChecker, MIN_PASSWORD_SCORE } from "src/components/base/pw-strength";

const PasswordStrength = lazy(() => import("src/components/base/pw-strength"));

/**
 * The properties for {@link CreatePasswordWithTotpDialog}
 */
export type CreatePasswordDialogProps = {
    /** Callback function when the dialog is submitted */
    onSubmit: () => void;
    /** Callback function when the dialog is closed */
    onClose: () => void;
    /** The users email address */
    mail: string;
    /** Whether the dialog is open */
    isOpen: boolean;
};

/**
 * Dialog for creating password
 */
export default function CreatePasswordWithTotpDialog(props: CreatePasswordDialogProps) {
    const [t] = useTranslation("authentication-dialog");
    const [tg] = useTranslation();

    const [secret] = React.useState(() => genTotpSecret());
    const url = genTotpUrl(props.mail, secret);

    const [step, setStep] = React.useState<"pw" | "totp">("pw");

    const form = useForm({
        defaultValues: {
            password: "",
            showPassword: false,

            label: "",
            token: "",
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                const res = await ApiUser.credentials.createPasswordWithTotp({
                    password: value.password,
                    label: value.label,
                    secret,
                    token: value.token,
                });
                if (res.result === "Err") {
                    return handleFormError(res.error, {
                        weak_password: (errors) => {
                            errors.fields.password = t("error.stronger-password");
                        },
                        token: (errors) => {
                            errors.fields.token = t("error.token-invalid");
                        },
                    });
                }
                toast.success(t("toast.success-create-password"));
                props.onSubmit();
            },
        },
    });

    const showPassword = useStore(form.store, (state) => state.values.showPassword);
    const isFieldsValid = useStore(form.store, (state) => state.isFieldsValid);

    useEffect(() => {
        form.reset();
    }, [props.isOpen, props.mail]);

    const passwordStrengthChecker = use(loadPasswordStrengthChecker());

    return (
        <Dialog onClose={props.onClose} open={props.isOpen}>
            <DialogTitle>{step === "pw" ? t("heading.create-password") : t("heading.create-totp-key")}</DialogTitle>
            <DialogBody>
                {step === "pw" && (
                    <Form
                        onSubmit={() => {
                            if (isFieldsValid) setStep("totp");
                        }}
                    >
                        <Fieldset>
                            <FieldGroup>
                                <form.Field
                                    name={"password"}
                                    validators={{
                                        onChange: ({ value }) => {
                                            if (
                                                value !== "" &&
                                                passwordStrengthChecker.check(value).score < MIN_PASSWORD_SCORE
                                            ) {
                                                return t("error.stronger-password");
                                            }
                                            return undefined;
                                        },
                                    }}
                                >
                                    {(fieldApi) => (
                                        <Field>
                                            <RequiredLabel>{t("label.new-password")}</RequiredLabel>
                                            <div className={"flex gap-3"}>
                                                <Input
                                                    autoComplete={"new-password"}
                                                    autoFocus={true}
                                                    required={true}
                                                    type={showPassword ? "text" : "password"}
                                                    invalid={fieldApi.state.meta.errors.length !== 0}
                                                    value={fieldApi.state.value}
                                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                                />
                                                <form.Field name={"showPassword"}>
                                                    {(field) => (
                                                        <Button
                                                            plain={true}
                                                            onClick={() => field.handleChange((prev) => !prev)}
                                                        >
                                                            {field.state.value ? <EyeSlashIcon /> : <EyeIcon />}
                                                        </Button>
                                                    )}
                                                </form.Field>
                                            </div>

                                            {fieldApi.state.meta.errors.map((error) => (
                                                <ErrorMessage className={"mt-3"}>{error}</ErrorMessage>
                                            ))}

                                            <Suspense fallback={<div />}>
                                                <PasswordStrength password={fieldApi.state.value} />
                                            </Suspense>
                                        </Field>
                                    )}
                                </form.Field>
                                <DialogActions>
                                    <PrimaryButton type={"submit"}>{tg("button.next")}</PrimaryButton>
                                </DialogActions>
                            </FieldGroup>
                        </Fieldset>
                    </Form>
                )}
                {step === "totp" && (
                    <Form onSubmit={form.handleSubmit}>
                        <Fieldset>
                            <FieldGroup>
                                <div className={"flex w-full justify-center"}>
                                    <a href={url}>
                                        <QRCodeSVG className={"size-60 rounded-lg bg-white p-2"} value={url} />
                                    </a>
                                </div>
                                <span className={"text-center"}>
                                    <Text>{tg("description.add-totp-secret")}</Text>
                                    <Code>{secret}</Code>
                                </span>
                                <form.Field name={"label"}>
                                    {(fieldApi) => (
                                        <Field className={"mt-4"}>
                                            <Label>{t("label.label")}</Label>
                                            <Text className={"my-1"}>{t("description.label-totp")}</Text>
                                            <Input
                                                required={true}
                                                type={"text"}
                                                autoFocus={true}
                                                value={fieldApi.state.value}
                                                onChange={(e) => fieldApi.handleChange(e.target.value)}
                                                invalid={fieldApi.form.state.errors.length !== 0}
                                            />
                                            {fieldApi.state.meta.errors.map((err) => (
                                                <ErrorMessage key={err}>{err}</ErrorMessage>
                                            ))}
                                        </Field>
                                    )}
                                </form.Field>

                                <form.Field name={"token"}>
                                    {(fieldApi) => (
                                        <Field>
                                            <Label>{t("label.totp-token")}</Label>
                                            <Text>{t("description.totp-token")}</Text>
                                            <Input
                                                className={"mt-1"}
                                                autoComplete={"one-time-code"}
                                                required={true}
                                                minLength={6}
                                                maxLength={8}
                                                value={fieldApi.state.value}
                                                onChange={(e) => fieldApi.handleChange(e.target.value)}
                                                invalid={fieldApi.form.state.errors.length !== 0}
                                            />
                                            {fieldApi.state.meta.errors.map((err) => (
                                                <ErrorMessage key={err}>{err}</ErrorMessage>
                                            ))}
                                        </Field>
                                    )}
                                </form.Field>
                                <DialogActions>
                                    <PrimaryButton type={"submit"}>{tg("button.save")}</PrimaryButton>
                                </DialogActions>
                            </FieldGroup>
                        </Fieldset>
                    </Form>
                )}
            </DialogBody>
        </Dialog>
    );
}
